import * as React from "react";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  MultiSelect,
  type MultiSelectOption,
} from "@repo/ui";
import { Calendar } from "lucide-react";
import { formatToApiDate } from "@repo/utils";
import {
  InventorySearchCombobox,
  type InventoryItem,
} from "@/components/forms/inventory-search-combobox";
import { DateInput } from "@/components/forms";

// ─── Types ───────────────────────────────────────────────────────────────────

interface User {
  id: string;
  initials: string;
  full_name: string | null;
  role: string;
}

interface Wholesaler {
  id: string;
  name: string;
}

export interface InventoryReportFilters {
  status?: string;
  drug_type?: string;
  show_hidden?: boolean;
}

export interface DispenseReportFilters {
  dateFrom?: string;
  dateTo?: string;
  userIds?: string;
  ndc?: string;
  itemName?: string;
  rxNumber?: string;
}

export interface ReceiptReportFilters {
  dateReceivedFrom?: string;
  dateReceivedTo?: string;
  wholesalerId?: string;
  userIds?: string;
  ndc?: string;
  itemName?: string;
  invoiceNumber?: string;
}

export interface ActivityReportFilters {
  startDate?: string;
  endDate?: string;
  transactionType?: string;
  ndc?: string;
  itemName?: string;
  userIds?: string;
}

export interface AuditReportFilters {
  action_timestampFrom?: string;
  action_timestampTo?: string;
  action_type?: string;
  table_name?: string;
  user_id?: string;
}

export type ReportFilterState =
  | InventoryReportFilters
  | DispenseReportFilters
  | ReceiptReportFilters
  | ActivityReportFilters
  | AuditReportFilters;

interface AsyncFilterData {
  users: User[];
  wholesalers: Wholesaler[];
  isLoadingUsers: boolean;
  isLoadingWholesalers: boolean;
}

// ─── Shared layout helper ────────────────────────────────────────────────────

function FilterGrid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {children}
    </div>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

// ─── Inventory Filters ───────────────────────────────────────────────────────

interface InventoryFiltersPanelProps {
  filters: InventoryReportFilters;
  onChange: (f: InventoryReportFilters) => void;
}

function InventoryFiltersPanel({
  filters,
  onChange,
}: InventoryFiltersPanelProps) {
  return (
    <FilterGrid>
      <FilterField label="Status">
        <Select
          value={filters.status || "all"}
          onValueChange={(v) =>
            onChange({ ...filters, status: v === "all" ? undefined : v })
          }
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="in_stock">In Stock</SelectItem>
            <SelectItem value="reorder_needed">Reorder Needed</SelectItem>
            <SelectItem value="sold_out">Sold Out</SelectItem>
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label="Drug Schedule">
        <MultiSelect
          options={[
            { value: "schedule_ii", label: "Schedule II" },
            { value: "schedule_iii", label: "Schedule III" },
            { value: "schedule_iv", label: "Schedule IV" },
            { value: "schedule_v", label: "Schedule V" },
            { value: "is_null", label: "Non-Controlled" },
          ]}
          selected={
            filters.drug_type
              ? filters.drug_type.split(",").filter(Boolean)
              : []
          }
          onChange={(vals) =>
            onChange({
              ...filters,
              drug_type: vals.length > 0 ? vals.join(",") : undefined,
            })
          }
          placeholder="All schedules"
        />
      </FilterField>

      <FilterField label="Visibility">
        <div className="flex h-9 items-center gap-2 rounded-md border border-input px-3">
          <Switch
            id="show-hidden"
            checked={filters.show_hidden ?? false}
            onCheckedChange={(v) => onChange({ ...filters, show_hidden: v })}
          />
          <Label htmlFor="show-hidden" className="text-sm cursor-pointer">
            Include hidden items
          </Label>
        </div>
      </FilterField>
    </FilterGrid>
  );
}

// ─── Dispenses Filters ───────────────────────────────────────────────────────

interface DispenseFiltersPanelProps {
  filters: DispenseReportFilters;
  onChange: (f: DispenseReportFilters) => void;
  users: User[];
  isLoadingUsers: boolean;
}

function DispenseFiltersPanel({
  filters,
  onChange,
  users,
  isLoadingUsers,
}: DispenseFiltersPanelProps) {
  const [selectedItem, setSelectedItem] = React.useState<InventoryItem | null>(
    null
  );

  const userOptions: MultiSelectOption[] = users.map((u) => ({
    value: u.id,
    label: u.full_name || u.initials,
  }));

  const selectedUserIds = filters.userIds
    ? filters.userIds.split(",").filter(Boolean)
    : [];

  const handleItemSelect = (item: InventoryItem | null) => {
    setSelectedItem(item);
    onChange({
      ...filters,
      ndc: item?.ndc || undefined,
      itemName: item?.itemName || undefined,
    });
  };

  return (
    <FilterGrid>
      <FilterField label="Start Date">
        <DateInput
          className="h-9"
          value={filters.dateFrom || ""}
          onChange={(v) => onChange({ ...filters, dateFrom: v || undefined })}
        />
      </FilterField>

      <FilterField label="End Date">
        <DateInput
          className="h-9"
          value={filters.dateTo || ""}
          onChange={(v) => onChange({ ...filters, dateTo: v || undefined })}
        />
      </FilterField>

      <FilterField label="Users">
        {isLoadingUsers ? (
          <div className="flex h-9 items-center justify-center rounded-md border border-input px-3 text-sm text-muted-foreground">
            Loading...
          </div>
        ) : (
          <MultiSelect
            options={userOptions}
            selected={selectedUserIds}
            onChange={(ids) =>
              onChange({
                ...filters,
                userIds: ids.length > 0 ? ids.join(",") : undefined,
              })
            }
            placeholder="All users"
          />
        )}
      </FilterField>

      <FilterField label="Inventory Item">
        <InventorySearchCombobox
          onSelect={handleItemSelect}
          placeholder={selectedItem ? selectedItem.itemName : "Search item..."}
          searchPlaceholder="Type to search..."
        />
      </FilterField>

      <FilterField label="RX Number">
        <Input
          className="h-9 font-mono"
          placeholder="Search by RX number..."
          value={filters.rxNumber || ""}
          onChange={(e) =>
            onChange({ ...filters, rxNumber: e.target.value || undefined })
          }
        />
      </FilterField>
    </FilterGrid>
  );
}

// ─── Receipts Filters ────────────────────────────────────────────────────────

interface ReceiptFiltersPanelProps {
  filters: ReceiptReportFilters;
  onChange: (f: ReceiptReportFilters) => void;
  users: User[];
  wholesalers: Wholesaler[];
  isLoadingUsers: boolean;
  isLoadingWholesalers: boolean;
}

function ReceiptFiltersPanel({
  filters,
  onChange,
  users,
  wholesalers,
  isLoadingUsers,
  isLoadingWholesalers,
}: ReceiptFiltersPanelProps) {
  const [selectedItem, setSelectedItem] = React.useState<InventoryItem | null>(
    null
  );

  const userOptions: MultiSelectOption[] = users.map((u) => ({
    value: u.id,
    label: u.full_name || u.initials,
  }));

  const selectedUserIds = filters.userIds
    ? filters.userIds.split(",").filter(Boolean)
    : [];

  const handleItemSelect = (item: InventoryItem | null) => {
    setSelectedItem(item);
    onChange({
      ...filters,
      ndc: item?.ndc || undefined,
      itemName: item?.itemName || undefined,
    });
  };

  return (
    <FilterGrid>
      <FilterField label="Start Date">
        <DateInput
          className="h-9"
          value={filters.dateReceivedFrom || ""}
          onChange={(v) =>
            onChange({
              ...filters,
              dateReceivedFrom: v || undefined,
            })
          }
        />
      </FilterField>

      <FilterField label="End Date">
        <DateInput
          className="h-9"
          value={filters.dateReceivedTo || ""}
          onChange={(v) =>
            onChange({
              ...filters,
              dateReceivedTo: v || undefined,
            })
          }
        />
      </FilterField>

      <FilterField label="Wholesaler">
        {isLoadingWholesalers ? (
          <div className="flex h-9 items-center justify-center rounded-md border border-input px-3 text-sm text-muted-foreground">
            Loading...
          </div>
        ) : (
          <Select
            value={filters.wholesalerId || "all"}
            onValueChange={(v) =>
              onChange({
                ...filters,
                wholesalerId: v === "all" ? undefined : v,
              })
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All wholesalers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Wholesalers</SelectItem>
              {wholesalers.map((w) => (
                <SelectItem key={w.id} value={w.id}>
                  {w.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FilterField>

      <FilterField label="Users">
        {isLoadingUsers ? (
          <div className="flex h-9 items-center justify-center rounded-md border border-input px-3 text-sm text-muted-foreground">
            Loading...
          </div>
        ) : (
          <MultiSelect
            options={userOptions}
            selected={selectedUserIds}
            onChange={(ids) =>
              onChange({
                ...filters,
                userIds: ids.length > 0 ? ids.join(",") : undefined,
              })
            }
            placeholder="All users"
          />
        )}
      </FilterField>

      <FilterField label="Inventory Item">
        <InventorySearchCombobox
          onSelect={handleItemSelect}
          placeholder={selectedItem ? selectedItem.itemName : "Search item..."}
          searchPlaceholder="Type to search..."
        />
      </FilterField>

      <FilterField label="Invoice Number">
        <Input
          className="h-9"
          placeholder="Search by invoice #..."
          value={filters.invoiceNumber || ""}
          onChange={(e) =>
            onChange({
              ...filters,
              invoiceNumber: e.target.value || undefined,
            })
          }
        />
      </FilterField>
    </FilterGrid>
  );
}

// ─── Activity Filters ────────────────────────────────────────────────────────

interface ActivityFiltersPanelProps {
  filters: ActivityReportFilters;
  onChange: (f: ActivityReportFilters) => void;
  users: User[];
  isLoadingUsers: boolean;
}

function ActivityFiltersPanel({
  filters,
  onChange,
  users,
  isLoadingUsers,
}: ActivityFiltersPanelProps) {
  const [selectedItem, setSelectedItem] = React.useState<InventoryItem | null>(
    null
  );

  const userOptions: MultiSelectOption[] = users.map((u) => ({
    value: u.id,
    label: u.full_name || u.initials,
  }));

  const handleItemSelect = (item: InventoryItem | null) => {
    setSelectedItem(item);
    onChange({
      ...filters,
      ndc: item?.ndc || undefined,
      itemName: item?.itemName || undefined,
    });
  };

  const selectedUserIds = filters.userIds
    ? filters.userIds.split(",").filter(Boolean)
    : [];

  const handleDatePreset = (preset: string) => {
    const today = new Date();
    const end = formatToApiDate(today);
    const start = new Date();

    switch (preset) {
      case "today":
        onChange({ ...filters, startDate: end, endDate: end });
        break;
      case "last7":
        start.setDate(today.getDate() - 7);
        onChange({
          ...filters,
          startDate: formatToApiDate(start),
          endDate: end,
        });
        break;
      case "last30":
        start.setDate(today.getDate() - 30);
        onChange({
          ...filters,
          startDate: formatToApiDate(start),
          endDate: end,
        });
        break;
      case "thisMonth":
        start.setDate(1);
        onChange({
          ...filters,
          startDate: formatToApiDate(start),
          endDate: end,
        });
        break;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDatePreset("today")}
        >
          <Calendar className="h-3 w-3 mr-1.5" />
          Today
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDatePreset("last7")}
        >
          Last 7 Days
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDatePreset("last30")}
        >
          Last 30 Days
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleDatePreset("thisMonth")}
        >
          This Month
        </Button>
      </div>

      <FilterGrid>
        <FilterField label="Start Date">
          <DateInput
            className="h-9"
            value={filters.startDate || ""}
            onChange={(v) =>
              onChange({ ...filters, startDate: v || undefined })
            }
          />
        </FilterField>

        <FilterField label="End Date">
          <DateInput
            className="h-9"
            value={filters.endDate || ""}
            onChange={(v) => onChange({ ...filters, endDate: v || undefined })}
          />
        </FilterField>

        <FilterField label="Transaction Type">
          <Select
            value={filters.transactionType || "all"}
            onValueChange={(v) =>
              onChange({
                ...filters,
                transactionType: v === "all" ? undefined : v,
              })
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="receipt">Receipts</SelectItem>
              <SelectItem value="dispense">Dispenses</SelectItem>
              <SelectItem value="adjustment">Adjustments</SelectItem>
            </SelectContent>
          </Select>
        </FilterField>

        <FilterField label="Inventory Item">
          <InventorySearchCombobox
            onSelect={handleItemSelect}
            placeholder={
              selectedItem ? selectedItem.itemName : "Search item..."
            }
            searchPlaceholder="Type to search..."
          />
        </FilterField>

        <FilterField label="Users">
          {isLoadingUsers ? (
            <div className="flex h-9 items-center justify-center rounded-md border border-input px-3 text-sm text-muted-foreground">
              Loading...
            </div>
          ) : (
            <MultiSelect
              options={userOptions}
              selected={selectedUserIds}
              onChange={(ids) =>
                onChange({
                  ...filters,
                  userIds: ids.length > 0 ? ids.join(",") : undefined,
                })
              }
              placeholder="All users"
            />
          )}
        </FilterField>
      </FilterGrid>
    </div>
  );
}

// ─── Audit Filters ───────────────────────────────────────────────────────────

interface AuditFiltersPanelProps {
  filters: AuditReportFilters;
  onChange: (f: AuditReportFilters) => void;
  users: User[];
  isLoadingUsers: boolean;
}

function AuditFiltersPanel({
  filters,
  onChange,
  users,
  isLoadingUsers,
}: AuditFiltersPanelProps) {
  return (
    <FilterGrid>
      <FilterField label="Start Date">
        <DateInput
          className="h-9"
          value={filters.action_timestampFrom || ""}
          onChange={(v) =>
            onChange({
              ...filters,
              action_timestampFrom: v || undefined,
            })
          }
        />
      </FilterField>

      <FilterField label="End Date">
        <DateInput
          className="h-9"
          value={filters.action_timestampTo || ""}
          onChange={(v) =>
            onChange({
              ...filters,
              action_timestampTo: v || undefined,
            })
          }
        />
      </FilterField>

      <FilterField label="Action Type">
        <Select
          value={filters.action_type || "all"}
          onValueChange={(v) =>
            onChange({
              ...filters,
              action_type: v === "all" ? undefined : v,
            })
          }
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="All actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="receipt_created">Receipt Created</SelectItem>
            <SelectItem value="receipt_edited">Receipt Edited</SelectItem>
            <SelectItem value="receipt_deleted">Receipt Deleted</SelectItem>
            <SelectItem value="dispense_created">Dispense Created</SelectItem>
            <SelectItem value="dispense_edited">Dispense Edited</SelectItem>
            <SelectItem value="dispense_deleted">Dispense Deleted</SelectItem>
            <SelectItem value="inventory_created">Item Added</SelectItem>
            <SelectItem value="inventory_updated">Item Updated</SelectItem>
            <SelectItem value="inventory_deleted">Item Deleted</SelectItem>
            <SelectItem value="stock_adjustment_created">
              Stock Adjustment Created
            </SelectItem>
            <SelectItem value="stock_adjustment_edited">
              Stock Adjustment Edited
            </SelectItem>
            <SelectItem value="stock_adjustment_deleted">
              Stock Adjustment Deleted
            </SelectItem>
            <SelectItem value="stock_manual_override">
              Stock Override Set
            </SelectItem>
            <SelectItem value="authorization_approved">
              Authorization Approved
            </SelectItem>
            <SelectItem value="authorization_denied">
              Authorization Denied
            </SelectItem>
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label="Table">
        <Select
          value={filters.table_name || "all"}
          onValueChange={(v) =>
            onChange({
              ...filters,
              table_name: v === "all" ? undefined : v,
            })
          }
        >
          <SelectTrigger className="h-9">
            <SelectValue placeholder="All tables" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tables</SelectItem>
            <SelectItem value="receipts">Receipts</SelectItem>
            <SelectItem value="dispenses">Dispenses</SelectItem>
            <SelectItem value="inventory">Inventory</SelectItem>
            <SelectItem value="stock_adjustments">Stock Adjustments</SelectItem>
            <SelectItem value="users">Users</SelectItem>
            <SelectItem value="authorization_approvals">
              Authorizations
            </SelectItem>
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label="User">
        {isLoadingUsers ? (
          <div className="flex h-9 items-center justify-center rounded-md border border-input px-3 text-sm text-muted-foreground">
            Loading...
          </div>
        ) : (
          <Select
            value={filters.user_id || "all"}
            onValueChange={(v) =>
              onChange({
                ...filters,
                user_id: v === "all" ? undefined : v,
              })
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.full_name || u.initials}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </FilterField>
    </FilterGrid>
  );
}

// ─── Dispatcher ──────────────────────────────────────────────────────────────

type ReportFiltersPanelProps =
  | ({
      source: "inventory";
      filters: InventoryReportFilters;
      onChange: (f: InventoryReportFilters) => void;
    } & AsyncFilterData)
  | ({
      source: "dispenses";
      filters: DispenseReportFilters;
      onChange: (f: DispenseReportFilters) => void;
    } & AsyncFilterData)
  | ({
      source: "receipts";
      filters: ReceiptReportFilters;
      onChange: (f: ReceiptReportFilters) => void;
    } & AsyncFilterData)
  | ({
      source: "activity";
      filters: ActivityReportFilters;
      onChange: (f: ActivityReportFilters) => void;
    } & AsyncFilterData)
  | ({
      source: "audit";
      filters: AuditReportFilters;
      onChange: (f: AuditReportFilters) => void;
    } & AsyncFilterData);

export function ReportFiltersPanel(props: ReportFiltersPanelProps) {
  switch (props.source) {
    case "inventory":
      return (
        <InventoryFiltersPanel
          filters={props.filters}
          onChange={props.onChange}
        />
      );
    case "dispenses":
      return (
        <DispenseFiltersPanel
          filters={props.filters}
          onChange={props.onChange}
          users={props.users}
          isLoadingUsers={props.isLoadingUsers}
        />
      );
    case "receipts":
      return (
        <ReceiptFiltersPanel
          filters={props.filters}
          onChange={props.onChange}
          users={props.users}
          wholesalers={props.wholesalers}
          isLoadingUsers={props.isLoadingUsers}
          isLoadingWholesalers={props.isLoadingWholesalers}
        />
      );
    case "activity":
      return (
        <ActivityFiltersPanel
          filters={props.filters}
          onChange={props.onChange}
          users={props.users}
          isLoadingUsers={props.isLoadingUsers}
        />
      );
    case "audit":
      return (
        <AuditFiltersPanel
          filters={props.filters}
          onChange={props.onChange}
          users={props.users}
          isLoadingUsers={props.isLoadingUsers}
        />
      );
  }
}
